import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ContributionItem,
  LobbyParticipant,
  OptionDistribution,
  QuestionView,
  RevealPayload,
  ScoreboardRow,
  ScaleAggregate,
  Locale,
  SessionSnapshot,
  SessionSounds,
} from '@livequiz/shared';
import { createSocket, type LiveSocket } from './socket.js';

type Phase = SessionSnapshot['phase'];
type State = SessionSnapshot['state'];

export interface PresenterState {
  connected: boolean;
  ready: boolean;
  error: string | null;
  sessionId: string | null;
  pin: string | null;
  joinUrl: string | null;
  state: State | null;
  phase: Phase;
  question: QuestionView | null;
  endsAt: number | null;
  clockOffset: number;
  countdown: number | null;
  answered: number;
  total: number;
  lobby: LobbyParticipant[];
  reveal: RevealPayload | null;
  scoreboard: ScoreboardRow[] | null;
  podium: ScoreboardRow[] | null;
  hardest: { index: number; prompt: string; correctRatio: number }[] | null;
  contributions: ContributionItem[] | null;
  words: { text: string; count: number }[] | null;
  scaleLive: ScaleAggregate | null;
  pollLive: OptionDistribution[] | null;
  reactions: { emoji: string; id: string }[];
  reactionCounts: Record<string, number>;
  teams: string[] | null;
  themeId: string;
  themeOverrides: Record<string, string> | null;
  locale: Locale;
  sounds: SessionSounds | null;
  paused: boolean;
  act: (event: 'host:start' | 'host:next' | 'host:skip' | 'host:pause' | 'host:resume' | 'host:end') => void;
  extend: (seconds: number) => void;
}

export function usePresenter(sessionId: string | null): PresenterState {
  const socketRef = useRef<LiveSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pin, setPin] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [state, setState] = useState<State | null>(null);
  const [phase, setPhase] = useState<Phase>(null);
  const [question, setQuestion] = useState<QuestionView | null>(null);
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [clockOffset, setClockOffset] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [answered, setAnswered] = useState(0);
  const [total, setTotal] = useState(0);
  const [lobby, setLobby] = useState<LobbyParticipant[]>([]);
  const [reveal, setReveal] = useState<RevealPayload | null>(null);
  const [scoreboard, setScoreboard] = useState<ScoreboardRow[] | null>(null);
  const [podium, setPodium] = useState<ScoreboardRow[] | null>(null);
  const [hardest, setHardest] = useState<PresenterState['hardest']>(null);
  const [contributions, setContributions] = useState<ContributionItem[] | null>(null);
  const [words, setWords] = useState<{ text: string; count: number }[] | null>(null);
  const [scaleLive, setScaleLive] = useState<ScaleAggregate | null>(null);
  const [pollLive, setPollLive] = useState<OptionDistribution[] | null>(null);
  const [reactions, setReactions] = useState<{ emoji: string; id: string }[]>([]);
  const [reactionCounts, setReactionCounts] = useState<Record<string, number>>({});
  const [teams, setTeams] = useState<string[] | null>(null);
  const [themeId, setThemeId] = useState('amt');
  const [locale, setLocale] = useState<Locale>('de');
  const [themeOverrides, setThemeOverrides] = useState<Record<string, string> | null>(null);
  const [sounds, setSounds] = useState<SessionSounds | null>(null);
  const [paused, setPaused] = useState(false);

  const applySnapshot = useCallback((snap: SessionSnapshot) => {
    setState(snap.state);
    setPhase(snap.phase);
    setQuestion(snap.question);
    setEndsAt(snap.endsAt);
    setClockOffset(snap.serverNow - Date.now());
    setReveal(snap.reveal);
    setScoreboard(snap.scoreboard);
    setContributions(snap.contributions);
    setThemeId(snap.themeId);
    setLocale(snap.locale);
    setThemeOverrides(snap.themeOverrides);
    setSounds(snap.sounds);
    setTeams(snap.teams);
    setTotal(snap.participantCount);
    setPaused(snap.state === 'paused');
    if (snap.lobby) setLobby(snap.lobby);
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    // Session-Infos (PIN, Join-URL) laden.
    fetch(`/api/sessions/${sessionId}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Session nicht gefunden oder keine Berechtigung.'))))
      .then((d: { pin: string; joinUrl: string }) => {
        setPin(d.pin);
        setJoinUrl(d.joinUrl);
      })
      .catch((e) => setError(e.message));

    const socket = createSocket();
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('host:join', { sessionId }, (r) => {
        if (r.ok) {
          setReady(true);
          applySnapshot(r.snapshot);
        } else setError(r.message);
      });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('lobby:update', ({ participants, count }) => {
      setLobby(participants);
      setTotal(count);
    });
    socket.on('question:countdown', ({ secondsLeft }) => {
      setState('running');
      setPhase('countdown');
      setCountdown(secondsLeft);
      setReveal(null);
      setAnswered(0);
      setContributions(null);
      setWords(null);
      setScaleLive(null);
      setPollLive(null);
    });
    socket.on('contributions:update', ({ items }) => setContributions(items));
    socket.on('wordcloud:update', ({ words: w }) => setWords(w));
    socket.on('scale:update', (agg) => setScaleLive(agg));
    socket.on('poll:update', ({ distribution }) => setPollLive(distribution));
    socket.on('question:show', ({ question: q, endsAt: e, serverNow }) => {
      setState('running');
      setPhase('answering');
      setQuestion(q);
      setEndsAt(e);
      setClockOffset(serverNow - Date.now());
      setAnswered(0);
      setReveal(null);
      setPaused(false);
      setScaleLive(null);
      setPollLive(null);
    });
    socket.on('answer:count', ({ answered: a, total: t }) => {
      setAnswered(a);
      setTotal(t);
    });
    socket.on('question:reveal', (r) => {
      setState('running');
      setPhase('revealed');
      setReveal(r);
    });
    socket.on('scoreboard:show', ({ rows }) => {
      setState('running');
      setPhase('scoreboard');
      setScoreboard(rows);
    });
    socket.on('session:ended', ({ podium: p, hardestQuestions }) => {
      setState('ended');
      setPhase(null);
      setPodium(p);
      setHardest(hardestQuestions);
    });
    socket.on('session:paused', () => setPaused(true));
    socket.on('session:resumed', () => setPaused(false));
    socket.on('reaction:show', ({ emoji, id }) => {
      setReactions((r) => [...r.slice(-40), { emoji, id }]);
      setReactionCounts((c) => ({ ...c, [emoji]: (c[emoji] ?? 0) + 1 }));
      setTimeout(() => setReactions((r) => r.filter((x) => x.id !== id)), 2800);
    });
    socket.on('error', ({ message }) => setError(message));

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [sessionId, applySnapshot]);

  const act = useCallback<PresenterState['act']>((event) => {
    // Dynamischer Eventname: bewusst locker getypt (alle host:*-Events nehmen nur einen Ack).
    (socketRef.current as unknown as { emit: (e: string, cb: () => void) => void } | null)?.emit(event, () => {});
  }, []);
  const extend = useCallback((seconds: number) => {
    socketRef.current?.emit('host:extendTime', { seconds }, () => {});
  }, []);
  return {
    connected, ready, error, sessionId, pin, joinUrl, state, phase, question, endsAt, clockOffset,
    countdown, answered, total, lobby, reveal, scoreboard, podium, hardest, contributions, words, scaleLive, pollLive, reactions, reactionCounts, teams, themeId, themeOverrides, locale, sounds, paused, act, extend,
  };
}
